import { setAuthToken } from '../../../utils/authSession';
import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import { FaApple } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';

import { MUTATION } from '../../../gql-operations';
import { Link } from 'react-router-dom';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { withRouter, useLocation } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';

import styled from 'styled-components';

import * as COLOR from '../../../appearance/Colors';

import customAnalytics from '../../../util/custom-analytics';
import Loader from 'react-loader-spinner';
import AuthLayout from '../AuthLayout';
import {
    clearPublishedFolderAttribution,
    consumePendingPublishedFolderCopy,
    getPublishedFolderAttribution
} from '../../../util/pendingPublishedFolderCopy';
import {
    useLastUsedAuthMethod,
    rememberLastUsedAuthMethod,
    rememberPendingSocialAuthMethod
} from '../../../util/lastAuthMethod';
import environment from '../../../env-config';

const Register = ({ history }) => {
    const {t: lnT} = useTranslation();

    const location = useLocation();
    const [referralId, setReferralId] = useState('');
    const [showReferral, setShowReferral] = useState(false);
    const [regResponse, setRegResponse] = useState(undefined);
    const [showPassword, setShowPassword] = useState(false);
    const [socialLoginError, setSocialLoginError] = useState('');
    const [socialProviders, setSocialProviders] = useState({ google: false, apple: false });
    const [lastUsedAuthMethod, setLastUsedAuthMethod] = useLastUsedAuthMethod();
    const apolloClient = useApolloClient();

    const [registerMutation, { loading }] = useMutation(MUTATION.REGISTER, {
        onCompleted: res => setRegResponse(res)
    });

    useEffect(() => {
        // Check for internal referral (from other Luckynote users)
        let referralIdLocal = localStorage.getItem('referralId');
        if (referralIdLocal) {
            setReferralId(referralIdLocal)
        } else {
            // getting the ID from the params (only 'referral', not 'ref')
            const referralIdParam = new URLSearchParams(location.search).get('referral');
            if (referralIdParam) {
                localStorage.setItem("referralId", referralIdParam);
                setReferralId(referralIdParam)
            }
        }
        // Note: external 'ref' parameter is handled separately via discoverySource in registration
    }, []);

    useEffect(() => {
        let cancelled = false;
        const apiBaseUrl = String(environment.REACT_BACKEND_URL || '').replace(/\/graphql\/?$/, '');
        if (!apiBaseUrl) return undefined;

        fetch(`${apiBaseUrl}/api/auth/providers`, { cache: 'no-store' })
            .then(response => (response.ok ? response.json() : {}))
            .then(providers => {
                if (!cancelled) {
                    setSocialProviders({
                        google: providers?.google === true,
                        apple: providers?.apple === true
                    });
                }
            })
            .catch(() => {
                if (!cancelled) setSocialProviders({ google: false, apple: false });
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const startSocialSignIn = provider => {
        const apiBaseUrl = String(environment.REACT_BACKEND_URL || '').replace(/\/graphql\/?$/, '');
        if (!apiBaseUrl) {
            setSocialLoginError(lnT('auth:socialSignInIsUnavailableBecauseThe'));
            return;
        }

        rememberPendingSocialAuthMethod(provider);
        window.location.assign(
            `${apiBaseUrl}/api/auth/${provider}/start?returnTo=${encodeURIComponent('/home')}`
        );
    };

    const _setLocalStorage = response => {
        const { register } = response;

        if (register && register.token && register.refreshToken) {
            console.log('SETTING LOCAL STORAGE');
            const { token, refreshToken, userData } = register;
            setAuthToken(token, { refreshToken });
            // Clean up referral data after successful registration
            localStorage.removeItem('referralId');
            localStorage.removeItem('externalRef');
            // localStorage.setItem('userData', JSON.stringify(userData))
        }
    };

    const loadingMessage = () => {
        if (loading) {
            return (
                <div style={{ justifyContent: 'center', alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
                    <ErrorText>{lnT('auth:creatingAccountPleaseWaitItMightTake')}</ErrorText>
                    <div style={{ marginTop: 8 }}>
                        <Loader
                            type="Puff"
                            // color="#00BFFF"
                            color={COLOR.SECONDARY_COLOR}
                            height={50}
                            width={50}
                        // timeout={3000} //3 secs
                        />
                    </div>
                </div>
            )
        }
    };

    // Handle successful registration - move side effects to useEffect
    useEffect(() => {
        const finishRegistration = async () => {
            rememberLastUsedAuthMethod('email');
            setLastUsedAuthMethod('email');
            _setLocalStorage(regResponse);
            customAnalytics.sign_up(regResponse.register);
            setRegResponse(undefined);

            try {
                const publishedAttribution = getPublishedFolderAttribution();
                const copiedFolder = await consumePendingPublishedFolderCopy(apolloClient);
                if (publishedAttribution?.token) {
                    customAnalytics.published_folder_signup_conversion({
                        token: publishedAttribution.token,
                        intent: publishedAttribution.lastIntent || publishedAttribution.firstIntent,
                        copiedFolderId: copiedFolder?.id,
                        copiedFolderTitle: copiedFolder?.title
                    });
                    clearPublishedFolderAttribution();
                }

                if (copiedFolder?.id) {
                    history.push(`/f/${copiedFolder.id}`);
                    return;
                }
            } catch (error) {
                console.error('Failed to copy pending published folder:', error);
            }

            history.push('/home');
        };

        if (regResponse && regResponse.register.__typename === 'AuthPayload') {
            finishRegistration();
        }
    }, [apolloClient, regResponse, history]);

    const renderResponseMessage = () => {
        if (regResponse) {
            switch (regResponse.register.__typename) {
                case 'AuthPayload':
                    // Success case - don't render anything, useEffect handles navigation
                    return null;
                case 'DbStartTransactionError':
                case 'EmailAlreadyTakenError':
                case 'RegisterInvalidInputError':
                case 'CustomRegistrationError':
                    return (
                        <div className="auth-alert auth-alert--error">
                            <span className="auth-alert-icon">⚠</span>
                            <span className="auth-alert-body">{regResponse.register.message}</span>
                        </div>
                    );
                case 'InternalServerError':
                    return (
                        <div className="auth-alert auth-alert--error">
                            <span className="auth-alert-icon">⚠</span>
                            <span className="auth-alert-body">{lnT('auth:serverErrorWeLlBeBackSoon')}</span>
                        </div>
                    );
                default:
                    return (
                        <div className="auth-alert auth-alert--error">
                            <span className="auth-alert-icon">⚠</span>
                            <span className="auth-alert-body">{lnT('common:unknownError')}</span>
                        </div>
                    );
            }
        }
        return null;
    };

    const renderReferralMessage = () => {
        if (referralId && !loading) {
            return <InfoText>{lnT('auth:referralCode')}<strong>{referralId}</strong>{lnT('auth:applied')}</InfoText>;
        }
    }

    return (
        <AuthLayout>
            <Formik
                enableReinitialize
                initialValues={{
                    fullName: '',
                    email: '',
                    password: '',
                    referral: referralId ? referralId : ""
                }}
                onSubmit={async (values, actions) => {
                    setRegResponse(undefined);
                    registerMutation({
                        variables: {
                            data: {
                                fullName: values.fullName,
                                email: values.email,
                                password: values.password,
                                settings: {
                                    usedDesktopWebApp: true
                                },
                                referralId: values.referral // Internal referral from 'referral' parameter only
                            }
                        }
                    });

                    actions.setSubmitting(false);
                }}
                validationSchema={Yup.object().shape({
                    fullName: Yup.string().min(2, lnT('auth:tooShort')).max(70, lnT('auth:tooLongMaximumLengthIs70_382a3980')).required(lnT('auth:required')),
                    email: Yup.string().email(lnT('auth:invalidEmail_8864165e')).required(lnT('auth:required')),
                    password: Yup.string().min(6, lnT('auth:minimumPasswordLengthIs6')).max(50, lnT('auth:maximumPasswordLengthIs50')).required(lnT('auth:required')),
                    referral: Yup.string()
                })}
            >
                {({ values, handleSubmit, handleChange, handleBlur, errors, touched }) => (
                    <>
                        <h1 className="auth-title">{lnT('auth:createYourAccount')}</h1>
                        <p className="auth-subtitle">{lnT('auth:startCapturingNotesLinksAndFilesIn')}</p>

                        {renderResponseMessage()}
                        {loadingMessage()}

                        {socialLoginError ? (
                            <div className="auth-alert auth-alert--error">
                                <span className="auth-alert-icon">⚠</span>
                                <span className="auth-alert-body">{socialLoginError}</span>
                            </div>
                        ) : null}

                        {!loading ?
                            <>
                                {(socialProviders.google || socialProviders.apple) ? (
                                    <>
                                        <div className="auth-social-buttons">
                                            {socialProviders.google ? (
                                                <button
                                                    type="button"
                                                    className="auth-social-button"
                                                    onClick={() => startSocialSignIn('google')}
                                                >
                                                    <FcGoogle className="auth-social-icon" aria-hidden="true" />{lnT('auth:continueWithGoogle')}{lastUsedAuthMethod === 'google' ? <span className="auth-last-used-badge">{lnT('auth:lastUsed')}</span> : null}
                                                </button>
                                            ) : null}
                                            {socialProviders.apple ? (
                                                <button
                                                    type="button"
                                                    className="auth-social-button"
                                                    onClick={() => startSocialSignIn('apple')}
                                                >
                                                    <FaApple className="auth-social-icon auth-social-icon--apple" aria-hidden="true" />{lnT('auth:continueWithApple')}{lastUsedAuthMethod === 'apple' ? <span className="auth-last-used-badge">{lnT('auth:lastUsed')}</span> : null}
                                                </button>
                                            ) : null}
                                        </div>
                                        <div className="auth-or-divider" aria-hidden="true"><span>{lnT('auth:or')}</span></div>
                                    </>
                                ) : null}

                                <form onSubmit={handleSubmit} className="auth-form">
                                    <div className="auth-field">
                                        <input
                                            name="fullName"
                                            type="text"
                                            onChange={handleChange}
                                            value={values.fullName}
                                            placeholder={lnT('settings:yourName')}
                                        />
                                        <ErrorMessage
                                            name="fullName"
                                            render={msg => (
                                                <div id="fullName" className="error">
                                                    {msg}
                                                </div>
                                            )}
                                        />
                                    </div>

                                    <div className="auth-field">
                                        <input
                                            name="email"
                                            type="email"
                                            onChange={handleChange}
                                            value={values.email}
                                            placeholder={lnT('common:email')}
                                            spellCheck="off"
                                            autoCapitalize="off"
                                        />
                                        <ErrorMessage
                                            name="email"
                                            render={msg => (
                                                <div id="email" className="error">
                                                    {msg}
                                                </div>
                                            )}
                                        />
                                    </div>

                                    <div className="auth-field">
                                        <div className="auth-field-input-wrap">
                                            <input
                                                name="password"
                                                type={showPassword ? "text" : "password"}
                                                onChange={handleChange}
                                                value={values.password}
                                                placeholder={lnT('common:password')}
                                                autoCapitalize="off"
                                                autoCorrect="off"
                                                style={{ paddingRight: 44 }}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
                                            </button>
                                        </div>
                                        <ErrorMessage
                                            name="password"
                                            render={msg => (
                                                <div id="password" className="error">
                                                    {msg}
                                                </div>
                                            )}
                                        />
                                    </div>

                                    <button type="submit" className="auth-submit" disabled={loading}>
                                        {loading ? lnT('auth:registering') : lnT('auth:register')}
                                        {!loading && lastUsedAuthMethod === 'email' ? <span className="auth-last-used-badge">{lnT('auth:lastUsed')}</span> : null}
                                    </button>
                                </form>

                                <hr className="auth-divider" />

                                <div className="auth-footer">{lnT('auth:alreadyHaveAnAccount')}<Link to={'/login'}>{lnT('auth:signIn')}</Link>
                                </div>

                                <ReferralToggle onClick={() => setShowReferral(!showReferral)}>
                                    {showReferral ? lnT('auth:hideReferralInput') : lnT('auth:haveAReferralCode')}
                                </ReferralToggle>

                                {showReferral && (
                                    <ReferralContainer>
                                        <input
                                            name="referral"
                                            type="text"
                                            onChange={handleChange}
                                            value={values.referral}
                                            placeholder={lnT('auth:enterYourReferralCode')}
                                            spellCheck="off"
                                            autoCapitalize="characters"
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                textAlign: 'center',
                                                width: '100%',
                                                fontSize: '16px',
                                                color: '#666'
                                            }}
                                        />
                                    </ReferralContainer>
                                )}
                            </>
                            : null}

                        {renderReferralMessage()}
                    </>
                )}
            </Formik>
        </AuthLayout>
    );
};

const ErrorText = styled.div`
    color: #666;
    text-align: center;
    margin-top: 8px;
    line-height: 20px;
    white-space: pre-line;
`;

const InfoText = styled.div`
    color: ${COLOR.GREEN};
    text-align: center;
    margin-top: 8px;
    line-height: 20px;
    white-space: pre-line;
`;

const ReferralToggle = styled.button`
    margin-top: 16px;
    padding: 12px;
    background-color: #f5f5f5;
    border-radius: 8px;
    border: none;
    color: #2b88d9;
    font-size: 14px;
    cursor: pointer;
    width: 100%;
    max-width: 400px;

    &:hover {
        background-color: #eee;
    }
`;

const ReferralContainer = styled.div`
    margin-top: 12px;
    padding: 16px;
    background-color: #f9f9f9;
    border-radius: 8px;
    border: 1px dashed #eee;
    width: 100%;
    max-width: 400px;
`;

export default withRouter(Register);
